import { useEffect } from 'react';
import config from '../config/config';
import EventDTO from '../models/event';
import { BASE_PIXEL_ID } from './useBasePixel';

interface UseEventPixelOptions {
  trackPageView?: boolean;
  enabled?: boolean;
}

// Pixel IDs already registered with fbq. Seeded with the base pixel so an event
// whose meta_pixel_id equals the base one (Avantti-owned events) is a clean
// no-op instead of a "[Meta Pixel] - Duplicate Pixel ID" console warning —
// fbq('track', …) already broadcasts to every registered pixel, so there is
// nothing extra to do for that case.
const initializedPixelIds = new Set<string>([BASE_PIXEL_ID]);

/**
 * Initializes the event-specific Meta Pixel so it receives all subsequent
 * fbq('track', …) calls alongside the base pixel.
 *
 * The fbq stub is created at module level by useBasePixel, so init runs
 * synchronously — no polling needed. This guarantees the event pixel is
 * registered before any tracking effects in the same component fire.
 */
const useEventPixel = (
  event: EventDTO | null | undefined,
  options: UseEventPixelOptions = {},
) => {
  const { trackPageView = false, enabled = true } = options;

  useEffect(() => {
    if (!enabled || !config.metaPixelEnabled || !event?.meta_pixel_id) {
      return;
    }

    const { fbq } = window;
    if (!fbq) {
      return;
    }

    const pixelId = event.meta_pixel_id;
    const isNewPixel = !initializedPixelIds.has(pixelId);
    if (isNewPixel) {
      fbq('set', 'autoConfig', false, pixelId);
      fbq('init', pixelId);
      initializedPixelIds.add(pixelId);
    }

    if (trackPageView) {
      fbq('track', 'PageView');
    } else if (isNewPixel) {
      // The base PageView fires from Providers at root hydration, BEFORE this
      // (streamed) client component mounts — so a freshly initialized event
      // pixel would never receive a PageView, which is exactly what producers
      // check to confirm their pixel is installed. trackSingle targets only
      // the new pixel so the base one isn't double-counted.
      fbq('trackSingle', pixelId, 'PageView');
    }
  }, [event?.meta_pixel_id, trackPageView, enabled]);
};

export default useEventPixel;
